// Predictive card search: ARIA 1.2 combobox + listbox with arrow keys, Enter, Esc.
// Usage inside a page:
//   state: {...comboState('hero')}
//   view:  comboView(state, 'hero', {placeholder, label})
//   actions: Object.assign(actions, comboActions('hero', {onPick, onEnter}))
import { html } from './singles-hub--core--html.js?v=c7c57de095';
import { api } from './singles-hub--api.js?v=c7c57de095';
import { money, debounce, pretty } from './singles-hub--format.js?v=c7c57de095';
import { cardArt, icon } from './singles-hub--components--ui.js?v=c7c57de095';

export const comboState = (k) => ({ [k + 'Q']: '', [k + 'Items']: [], [k + 'Idx']: -1, [k + 'Open']: false, [k + 'Off']: false, [k + 'Busy']: false });

export function comboView(s, k, { placeholder = 'Search any card…', label = 'Search cards', footer = null } = {}) {
  const items = s[k + 'Items'], idx = s[k + 'Idx'], open = s[k + 'Open'] && (items.length > 0 || s[k + 'Q'].trim().length >= 2);
  const lb = 'mcc-' + k + '-lb';
  return html`<div class="mcc-search" data-popover-root>
    <span class="mcc-search__icon">${icon.search}</span>
    <label class="mcc-sr" for="mcc-${k}-input">${label}</label>
    <input id="mcc-${k}-input" class="mcc-input" type="search" role="combobox" autocomplete="off" spellcheck="false"
      aria-autocomplete="list" aria-expanded="${open ? 'true' : 'false'}" aria-controls="${lb}"
      ${idx >= 0 && open ? html`aria-activedescendant="${lb}-${idx}"` : ''}
      placeholder="${placeholder}" value="${s[k + 'Q']}" data-input="${k}Input" data-keydown="${k}Key" data-focus="${k}Focus" data-blur="${k}Blur">
    ${open ? html`<div class="mcc-listbox" data-mcc-popover data-open="true">
      <button type="button" hidden data-popover-close data-click="${k}Close"></button>
      <ul id="${lb}" role="listbox" aria-label="Card suggestions">
        ${items.length ? items.map((it, i) => html`<li id="${lb}-${i}" role="option" class="mcc-option" aria-selected="${i === idx ? 'true' : 'false'}" data-click="${k}Pick" data-i="${i}">
          ${cardArt(it.image_url, { name: it.name, size: 'thumb', w: 80 })}
          <span style="min-width:0"><span class="mcc-option__name">${pretty(it.name)}</span><span class="mcc-option__meta" style="display:block">${it.meta}</span></span>
          <span class="mcc-option__price">${it.from_price != null ? 'from ' + money(it.from_price) : ''}</span></li>`)
        : html`<li class="mcc-option" role="option" aria-disabled="true" aria-selected="false"><span class="mcc-option__meta">${s[k + 'Busy'] ? 'Searching…' : s[k + 'Off'] ? 'Card search is offline. Press Enter to search the store.' : 'No cards match yet. Press Enter to search.'}</span></li>`}
      </ul>${footer ? html`<div class="mcc-listbox__foot">${footer}</div>` : ''}</div>` : ''}
  </div>`;
}

export function comboActions(k, { onPick, onEnter, comp }) {
  let seq = 0;
  const fetchSug = debounce(async (c, q) => {
    const my = ++seq;
    if (q.trim().length < 2) { c.setState({ [k + 'Items']: [], [k + 'Idx']: -1 }); return; }
    try {
      const r = await api.suggest(q.trim());
      if (my !== seq) return;
      c.setState({ [k + 'Items']: r.items || [], [k + 'Idx']: -1, [k + 'Off']: false, [k + 'Busy']: false });
    } catch (e) { if (my === seq) c.setState({ [k + 'Items']: [], [k + 'Off']: !!e.offline, [k + 'Busy']: false }); }
  }, 140);
  return {
    [k + 'Input']: (el, e, c) => { c.setState({ [k + 'Q']: el.value, [k + 'Open']: true, [k + 'Busy']: el.value.trim().length >= 2 }); fetchSug(c, el.value); },
    [k + 'Focus']: (el, e, c) => c.setState({ [k + 'Open']: true }),
    [k + 'Blur']: (el, e, c) => setTimeout(() => { if (!c.root.contains(document.activeElement) || document.activeElement.id !== 'mcc-' + k + '-input') c.setState({ [k + 'Open']: false, [k + 'Idx']: -1 }); }, 180),
    [k + 'Close']: (el, e, c) => c.setState({ [k + 'Open']: false, [k + 'Idx']: -1 }),
    [k + 'Pick']: (el, e, c) => { const it = c.state[k + 'Items'][+el.dataset.i]; c.setState({ [k + 'Open']: false, [k + 'Idx']: -1 }); if (it) onPick(it, c); },
    [k + 'Key']: (el, e, c) => {
      const s = c.state, n = s[k + 'Items'].length;
      if (e.key === 'ArrowDown') { e.preventDefault(); if (n) c.setState({ [k + 'Open']: true, [k + 'Idx']: (s[k + 'Idx'] + 1) % n }); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); if (n) c.setState({ [k + 'Idx']: (s[k + 'Idx'] - 1 + n) % n }); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        const it = s[k + 'Idx'] >= 0 ? s[k + 'Items'][s[k + 'Idx']] : null;
        c.setState({ [k + 'Open']: false, [k + 'Idx']: -1 });
        if (it) onPick(it, c); else onEnter(el.value, c);
      } else if (e.key === 'Escape' && s[k + 'Open']) { e.stopPropagation(); c.setState({ [k + 'Open']: false, [k + 'Idx']: -1 }); }
    },
  };
}
